import { useTranslation } from 'react-i18next';
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { DataTable } from '@/components/ui/data-table';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { FileUpload, Loading } from '@/components/shared';
import { ConnectSourcesControls } from '@/components/shared/OutlookSyncProgress';
import { useDocuments } from '@/hooks/useDocuments';
import { useDeepAnalysisAvailability } from '@/hooks/useDeepAnalysisAvailability';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { backendClient } from '@/lib/api-client';
import { buildDeleteQueryString, getDeleteAttachmentCount } from '@/lib/deleteOptions';
import { ChevronLeft, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ColumnDef, Row } from "@tanstack/react-table";
import { Document } from '@/hooks/useDocuments';
import { DataTableFacetedFilter } from '@/components/ui/data-table-faceted-filter';
import { deriveFilterFields, type FilterField } from '@/components/ui/data-table-filter-toolbar';
import { ColumnsCaseFiles } from '@/components/ui/columns-case-files';
import { SortButton } from '@/components/ui/column-builders';
import { DEEP_ANALYSIS_LABEL } from '@/hooks/useAnalysisSet';
import { FileUploadHandles } from '@/components/shared/FileUpload';
import { SUPPORTED_UPLOAD_FILE_TYPES_STRING } from '@/constants/supported-file-types';
import { ExpandedAttachmentRows, canRowExpand } from '@/components/correspondence/ExpandedAttachmentRows';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import type { DocumentDeleteOptions } from '@/types/document';

// Create a document type column definition with options
const createDocumentTypeColumn = (
  options: { label: string; value: string }[],
  slug: string,
  deepAnalysisOnly: boolean,
): ColumnDef<Document> => ({
  id: "document_type",
  accessorFn: (row) => row.document_type?.name || 'Unknown',
  meta: { filter: { type: "multi", label: "Folder", options } },
  header: ({ column, table }) => {
    return (
      <div className='flex flex-row items-center whitespace-nowrap'>
        <span>Folder</span>
        <SortButton column={column} />
        {!table.options.meta?.filtersInToolbar && (
          <DataTableFacetedFilter
            column={column}
            title="Folder"
            options={options}
          />
        )}
      </div>
    )
  },
  cell: ({ row }) => {
    const docType = row.getValue("document_type") as string;
    const folderGuid = row.original.document_type?.guid;
    return (
      <div className="flex items-center">
        {folderGuid ? (
          <Link
            to="/matter/$slug/case-files/folder/$folderGuid"
            params={{ slug, folderGuid }}
            search={deepAnalysisOnly ? { view: 'deep-analysis' } : {}}
            className="hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {docType}
          </Link>
        ) : (
          <span>{docType}</span>
        )}
      </div>
    );
  },
  filterFn: (row, id, value) => {
    const docType = row.getValue(id) as string;
    if (!value || value.length === 0) return true;
    return value.includes(docType);
  },
  enableSorting: true,
});

const AllFilesView = () => {
  const { t } = useTranslation('common');
  const { selectedCase } = useCaseContext();
  const { openDocumentPanel } = useSidePanel();
  const navigate = useNavigate();
  const { slug } = useParams({ from: '/matter/$slug/case-files/all-files' });
  const search = useSearch({ from: '/matter/$slug/case-files/all-files' });
  const { isEnabled: deepAnalysisEnabled } = useDeepAnalysisAvailability(slug as string | undefined);
  const deepAnalysisOnly = deepAnalysisEnabled && search.view === 'deep-analysis';
  const setDeepAnalysisOnly = useCallback((nextValue: boolean) => {
    navigate({
      to: '/matter/$slug/case-files/all-files',
      params: { slug },
      search: nextValue ? { view: 'deep-analysis' } : {},
    });
  }, [navigate, slug]);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileUploadRef = useRef<FileUploadHandles>(null);
  useShellHeader({
    header: 'All Files',
  });
  
  const timezone = getTimezone();
  
  // Use the custom hook for document data, without filtering by document type
  const { documents, error, mutate, isLoading } = useDocuments(
    slug as string,
    undefined, // documentTypes
    undefined, // isDraft
    undefined, // folderIds - show all documents
    {},
    timezone,
    { analysisSetOnly: deepAnalysisOnly }
  );

  const handleDelete = async (selectedIds: string[], options?: DocumentDeleteOptions) => {
    if (!slug || !documents) return;

    const queryString = buildDeleteQueryString(options);

    try {
      await backendClient.post(`/correspondence/${slug}/delete-files${queryString}`, selectedIds);
      await mutate();
      setSelectedRows([]);
    } catch (err: any) {
      await mutate();
      throw err;
    }
  };

  const handleProcessingComplete = useCallback(() => {
    // Force a revalidation of the data when processing is complete
    mutate();
  }, [mutate]);

  const handleDropFiles = useCallback(async (files: File[]) => {
    if (!slug || isUploading) return;

    // Open the FileUpload dialog with the dropped files
    // This will use the new UploadProgressCenter flow
    if (fileUploadRef.current) {
      fileUploadRef.current.openWithFiles(files);
    }
  }, [slug, isUploading]);

  // Get document type options for filter
  const getDocTypeOptions = useCallback(() => {
    if (!documents) return [];
    
    const docTypes = new Set<string>();
    
    // Add Unknown type for documents without a document type
    let hasUnknownType = false;
    
    documents.forEach(doc => {
      if (doc.document_type?.name) {
        docTypes.add(doc.document_type.name);
      } else {
        hasUnknownType = true;
      }
    });
    
    const options = Array.from(docTypes).map(type => ({
      label: type,
      value: type
    }));
    
    // Add Unknown option if needed
    if (hasUnknownType) {
      options.push({
        label: 'Unknown',
        value: 'Unknown'
      });
    }
    
    // Sort options alphabetically
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [documents]);

  // Document type options state
  const [docTypeOptions, setDocTypeOptions] = useState<Array<{ label: string; value: string }>>([]);
  
  // Update document type options when documents change
  useEffect(() => {
    if (documents && documents.length > 0) {
      setDocTypeOptions(getDocTypeOptions());
    }
  }, [documents, getDocTypeOptions]);

  // Create custom columns with document type
  const getColumnsWithDocType = useCallback(() => {
    const baseColumns = ColumnsCaseFiles({
      refreshData: () => mutate(),
      documentTypeSlug: "all-files",
      handleDelete: handleDelete,
      slug: slug as string | undefined,
      deepAnalysisEnabled,
      openDocumentPanel
    });
    const documentTypeColumn = createDocumentTypeColumn(docTypeOptions, slug as string, deepAnalysisOnly);
    
    // If summary column not found, insert after fileName
    const fileNameIndex = baseColumns.findIndex(
      col => "accessorKey" in col && col.accessorKey === "fileName"
    );
    
    if (fileNameIndex !== -1) {
      return [
        ...baseColumns.slice(0, fileNameIndex + 1),
        documentTypeColumn,
        ...baseColumns.slice(fileNameIndex + 1)
      ];
    }
    
    // Fallback - just append the document type column
    return [...baseColumns, documentTypeColumn];
  }, [docTypeOptions, mutate, handleDelete, slug, deepAnalysisEnabled, openDocumentPanel, deepAnalysisOnly]);

  // Create columns with the current document type options
  const columns = useMemo(() => getColumnsWithDocType(), [getColumnsWithDocType]);

  // Filters menu config: column-driven fields (from each column's `meta.filter`)
  // plus the Deep Analysis view scope folded in as a single choice field.
  const filterFields = useMemo<FilterField[]>(() => {
    const fields = deriveFilterFields(columns);
    if (deepAnalysisEnabled) {
      fields.push({
        id: 'analysisView',
        label: 'View',
        type: 'choice',
        options: [
          { label: 'All files', value: 'all' },
          { label: `${DEEP_ANALYSIS_LABEL} only`, value: 'analysis' },
        ],
        value: deepAnalysisOnly ? 'analysis' : 'all',
        defaultValue: 'all',
        onChange: (value) => setDeepAnalysisOnly(value === 'analysis'),
      });
    }
    return fields;
  }, [columns, deepAnalysisEnabled, deepAnalysisOnly, setDeepAnalysisOnly]);

  const handleEmptyStateClick = () => {
    fileUploadRef.current?.openDialog();
  };

  // Render expanded attachment rows as actual <tr> elements for column alignment
  const renderSubRows = useCallback((row: Row<Document>) => (
    <ExpandedAttachmentRows
      row={row}
      slug={slug as string}
      onDocumentClick={(guid) => openDocumentPanel({ documentGuid: guid, activeTab: 'document' })}
      tableType="all-files"
    />
  ), [slug, openDocumentPanel]);

  return (
    <div className="overflow-x-hidden">
      {typeof selectedCase === 'undefined' ? (
        <NoCaseSelected />
      ) : (
        <>
          <div className="px-6 py-6 relative z-20">
            <div className="mb-4">
              <Button
                variant="ghost"
                onClick={() => navigate({
                  to: '/matter/$slug/case-files',
                  params: { slug },
                  search: deepAnalysisOnly ? { view: 'deep-analysis' } : {},
                })}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                <Folder className="mr-1 h-4 w-4" />
                Back to folders
              </Button>
            </div>
            
            {/* DataTable with horizontally aligned controls */}
            {error ? (
              <div>Error loading document data</div>
            ) : !documents || documents === undefined ? (
              <div className="flex flex-col gap-2 justify-center items-center h-full">
                <Loading />
                <span className="text-sm font-medium text-gray-700 relative z-10">Loading documents...</span>
              </div>
            ) : (
              <div className="pt-2 sm:pt-4 lg:pt-6">
              <DataTable
                columns={columns}
                data={documents}
                hasPopupMenu={true}
                uploadButton={false}
                exportButton={false}
                documentType="All Files"
                documentTypeSlug="all-files"
                searchPlaceholder={"Find documents..."}
                enableFilenameSuggestions
                onUploadComplete={handleProcessingComplete}
                onRowsSelected={(rows) => setSelectedRows(rows)}
                onDelete={handleDelete}
                getDeleteAttachmentCount={getDeleteAttachmentCount}
                isDeleting={false}
                isLoading={isLoading}
                onDropFiles={handleDropFiles}
                isProcessingDrop={isUploading}
                onEmptyStateClick={handleEmptyStateClick}
                renderSubRows={renderSubRows}
                getRowCanExpand={canRowExpand}
                onRowActivate={(row) => openDocumentPanel({ documentGuid: row.id, activeTab: 'document' })}
                onRowDoubleClick={(row) => openDocumentPanel({ documentGuid: row.id, activeTab: 'document' })}
                deepAnalysisEnabled={deepAnalysisEnabled}
                filterFields={filterFields}
                syncButton={
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex gap-2 items-center">
                      <ConnectSourcesControls matterId={slug as string} />
                      <FileUpload
                        ref={fileUploadRef}
                        matterGuid={slug as string}
                        onUploadComplete={handleProcessingComplete}
                        showAsDialog={true}
                        buttonText="Upload Files"
                        acceptTypes={SUPPORTED_UPLOAD_FILE_TYPES_STRING}
                        onProcessingStateChange={setIsUploading}
                      />
                    </div>
                  </div>
                }
              />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
export default AllFilesView;
